import { create } from 'zustand';
import { supabase } from './supabase';
import { queryClient } from './queryClient';
import { type AccountMembership, writePersistedAccountId } from './accountSelection';

export type SessionStatus = 'loading' | 'unauthenticated' | 'authenticated';

export type { AccountMembership, AccountRole } from './accountSelection';

interface SessionState {
  status: SessionStatus;

  // ----- user identity (auth.users) -----------------------------------------
  userId: string | null;
  email: string | null;
  displayName: string | null;
  /**
   * True only when the profile read SUCCEEDED and the name is blank — a member
   * needs a display name (vendor#1436), so `RequireAuth` asks for one before
   * the app. Deliberately NOT derived from `displayName === null`: that is also
   * what an unreadable profile row produces, and a read failure must never lock
   * a signed-in user behind a form.
   */
  displayNameMissing: boolean;

  // ----- the accounts this user holds an active seat on ---------------------
  // Vendor model (post multi-seat): access is granted by `account_members`, not
  // by `accounts.owner_user_id`. `current_account_ids()` resolves auth.uid()
  // through that table, so one user can hold seats on several accounts and
  // `currentAccountId` is a persisted SELECTION rather than a derivation
  // (docs/multi-seat-accounts.md §4.4). There is still no location concept.
  //
  // Sorted by name then id by `sessionBootstrap`, so `accounts[0]` is a stable
  // fallback. Exactly one entry is the overwhelmingly common case — 90% of
  // vendors work alone — and every surface that keys off multi-account state
  // must key off `accounts.length`, never off "multi-seat exists as a feature".
  accounts: readonly AccountMembership[];

  // The account every staff query scopes by. INVARIANT: whenever this is
  // non-null it is the id of an entry in `accounts` — see `accountSelection.ts`.
  currentAccountId: string | null;
  shopName: string | null;

  // ----- active bin for scan-to-add (defaults to null = use Processing bin) -
  activeBinId: string | null;

  // ----- actions ------------------------------------------------------------
  /** Bulk-set the session — called by sessionBootstrap on auth state changes. */
  setSession: (s: Partial<SessionState>) => void;
  /**
   * Switch the active account and persist the choice.
   *
   * Ignores an id that is not in `accounts`: the only ids this store will ever
   * scope a query by are ones the server confirmed an active seat for on the
   * last hydrate. A no-op is the correct response to an unknown id — silently
   * accepting it would put a value the user may have no seat on into every
   * subsequent `.eq('account_id', …)`.
   */
  setCurrentAccount: (accountId: string) => void;
  /** Set the active bin for scan-to-add. null = use the account's Processing bin. */
  setActiveBin: (id: string | null) => void;
  /** Reset to unauthenticated and let RequireAuth redirect. */
  clear: () => void;
  /**
   * Sign the user out of Supabase, reset the store, AND clear the TanStack
   * Query cache (`lib/queryClient.ts`) — unlike an account switch, a
   * different `auth.users` row may sign in next on the same device, and
   * nothing else scopes every query key by user. See `queryClient.ts`'s
   * docblock for why this matters.
   */
  signOut: () => Promise<void>;
}

const initialState: Pick<
  SessionState,
  | 'status'
  | 'userId'
  | 'email'
  | 'displayName'
  | 'displayNameMissing'
  | 'accounts'
  | 'currentAccountId'
  | 'shopName'
  | 'activeBinId'
> = {
  status: 'loading',
  userId: null,
  email: null,
  displayName: null,
  displayNameMissing: false,
  accounts: [],
  currentAccountId: null,
  shopName: null,
  activeBinId: null,
};

/**
 * Global auth + account store. Populated by `sessionBootstrap` once Supabase
 * resolves the user's session; consumed by `RequireAuth`, `DesktopLayout`
 * (avatar / workspace pill / account switcher), and feature pages that need
 * `currentAccountId` for RLS-scoped queries.
 *
 * Client state only (Zustand). Server state belongs to TanStack Query — and
 * because every account-scoped query key already carries `currentAccountId`
 * (e.g. `allBinsQueryKey(accountId)`), switching ACCOUNTS changes those keys
 * and the cache re-scopes itself. Nothing about an account switch clears the
 * query cache, and nothing should: the previous account's entries stay valid
 * under their own keys for when the user switches back.
 *
 * **Corrected 2026-08-12 (PR #791 fix round).** This paragraph used to end
 * "Nothing here clears the query cache, and nothing should" — true for an
 * account switch, false for `signOut()`. `signOut()` is a different USER
 * replacing this one on the same device (the shared-booth-device case), not
 * the same user picking a different seat, and a query key that does not
 * carry the user's id (the Vault's did not, until this same round) would
 * keep serving the outgoing user's cached rows to the incoming one for up to
 * `staleTime`. See `lib/queryClient.ts` for the full reasoning; `signOut`
 * below now clears it.
 *
 * Note the store deliberately does NOT reset on sign-out beyond the in-memory
 * fields — the persisted account selection survives in `localStorage` and is
 * re-validated on the next hydrate.
 */
export const useSession = create<SessionState>((set, get) => ({
  ...initialState,

  setSession: (s) => set(s),

  setCurrentAccount: (accountId) => {
    const match = get().accounts.find((a) => a.id === accountId);
    if (match == null) return;
    writePersistedAccountId(match.id);
    set({ currentAccountId: match.id, shopName: match.name });
  },

  setActiveBin: (id) => set({ activeBinId: id }),

  clear: () => set({ ...initialState, status: 'unauthenticated' }),

  signOut: async () => {
    await supabase.auth.signOut();
    set({ ...initialState, status: 'unauthenticated' });
    queryClient.clear();
  },
}));

interface UIState {
  navbarCollapsed: boolean;
  toggleNavbar: () => void;
}

export const useUI = create<UIState>((set) => ({
  navbarCollapsed: false,
  toggleNavbar: () => set((s) => ({ navbarCollapsed: !s.navbarCollapsed })),
}));
